/**
 * Team Leaderboard — HX Design Language. Org-scoped productivity rankings
 * for Admins / Supervisors: podium, KPI band, ranked tables, daily trend.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DataTable from "@/components/DataTable";
import {
  Trophy, Phone, ChatText, Clock, CheckCircle, PhoneIncoming,
  PhoneOutgoing, PhoneX, Timer, Calendar,
} from "@phosphor-icons/react";
import { HxCard, HxCardHead, HxButton, HxChip, HxSkeleton, useCountUp, HX_COLORS as C } from "@/hx";

const PRESETS = [
  { id: "today", label: "Today" },
  { id: "week",  label: "This Week" },
  { id: "month", label: "This Month" },
  { id: "custom", label: "Custom" },
];

const fmtDuration = (s) => {
  s = Math.round(s || 0);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  if (m < 60) return `${m}m ${sec}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const initials = (name) => (name || "?").trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();

export default function Leaderboard() {
  const { user } = useAuth();
  const [range, setRange] = useState("week");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const params = new URLSearchParams({ range, compare: "previous", limit: "10" });
      if (range === "custom") {
        if (!from || !to) { setErr("Pick a from and to date"); setLoading(false); return; }
        params.set("from", from);
        params.set("to", to);
      }
      const { data } = await api.get(`/leaderboard?${params.toString()}`);
      setData(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (range !== "custom") load(); }, [range]);

  const orgName = data?.org?.name || user?.organization?.name || "your team";

  return (
    <div className="hbf-hx" data-testid="leaderboard-page">
      {/* ── Hero ─────────────────────────────────────────────── */}
      <header className="hx-hero hx-up" data-testid="leaderboard-hero">
        <div>
          <div className="hx-hero__eyebrow">
            <Trophy size={16} weight="duotone" color={C.blue} /> Team leaderboard · {orgName}
          </div>
          <h1 className="hx-hero__title">Team productivity, <em>ranked</em></h1>
          <p className="hx-hero__sub">
            Who's leading the conversation
            {data && <> — {data.window.start.slice(0, 10)} → {data.window.end.slice(0, 10)}</>}
          </p>
        </div>
        <div className="hx-hero__cta" style={{ alignItems: "flex-end", flexWrap: "wrap" }} data-testid="leaderboard-filter">
          <div className="hx-seg" role="tablist">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                onClick={() => setRange(p.id)}
                className={`hx-seg__btn ${range === p.id ? "hx-seg__btn--on" : ""}`}
                data-testid={`range-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {range === "custom" && (
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="hx-input" style={{ width: 148 }} data-testid="from-date" />
              <Calendar size={14} color="var(--hx-ink-3)" />
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="hx-input" style={{ width: 148 }} data-testid="to-date" />
              <HxButton variant="primary" size="sm" onClick={load} disabled={loading} data-testid="apply-range">
                {loading ? "Loading…" : "Apply"}
              </HxButton>
            </div>
          )}
        </div>
      </header>

      {err && (
        <HxCard className="hx-up" style={{ padding: "14px 20px", borderColor: "#F5C2C2", background: "var(--hx-red-soft)", color: "#B91C1C", fontSize: 13, fontWeight: 600 }} data-testid="leaderboard-error">
          {err}
        </HxCard>
      )}
      {!data && !err && (
        <div data-testid="leaderboard-loading">
          <div className="hx-grid-kpi"><HxSkeleton height={150} /><HxSkeleton height={150} /><HxSkeleton height={150} /><HxSkeleton height={150} /></div>
          <HxSkeleton height={220} />
        </div>
      )}

      {data && (
        <>
          <Podium rows={data.top_callers || []} />
          <KpiBand kpis={data.kpis} prev={data.compare?.kpis} />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mt-5 hx-up hx-d3">
            <TopCallers rows={data.top_callers} />
            <TopSmsUsers rows={data.top_sms_users} />
          </div>
          <CallTrend series={data.daily_series} />
        </>
      )}
    </div>
  );
}

/* ============================================================
 *  Top performer podium — silver / GOLD / bronze
 * ============================================================ */
const PODIUM = [
  { medal: "Gold",   ring: "#F5B301", grad: "linear-gradient(165deg, #FFF8E4, #FFFFFF 58%)", chip: "linear-gradient(180deg, #FFD84D, #F0A800)" },
  { medal: "Silver", ring: "#B7C2D0", grad: "linear-gradient(165deg, #F3F6FB, #FFFFFF 58%)", chip: "linear-gradient(180deg, #D8E0EB, #A9B6C7)" },
  { medal: "Bronze", ring: "#D08A4F", grad: "linear-gradient(165deg, #FBF0E6, #FFFFFF 58%)", chip: "linear-gradient(180deg, #E3AC7C, #C0763B)" },
];

function PodiumCard({ r, rank }) {
  const p = PODIUM[rank];
  const calls = useCountUp(r.calls || 0);
  return (
    <HxCard
      hover
      className={`hx-podium ${rank === 0 ? "hx-podium--first" : ""}`}
      style={{ background: p.grad }}
      data-testid={`podium-${rank + 1}`}
    >
      <div className="hx-podium__chip" style={{ background: p.chip }}>#{rank + 1} {p.medal}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 15 }}>
        {r.unknown ? (
          <div className="hx-podium__avatar" style={{ background: "var(--hx-bg)", border: `2px solid ${p.ring}`, color: "var(--hx-ink-3)" }}>
            <Phone size={22} weight="duotone" />
          </div>
        ) : r.avatar_url ? (
          <img src={r.avatar_url} alt="" className="hx-podium__avatar" style={{ border: `2px solid ${p.ring}`, objectFit: "cover" }} />
        ) : (
          <div className="hx-podium__avatar" style={{ background: p.chip, border: `2px solid ${p.ring}` }}>{initials(r.name)}</div>
        )}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 16.5, fontWeight: 800, letterSpacing: "-0.015em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</div>
          {r.unknown ? (
            <>
              {r.phone && <div style={{ fontFamily: "var(--hx-mono)", fontSize: 11.5, color: "var(--hx-ink-2)", marginTop: 2 }}>{r.phone}</div>}
              <HxChip tone="gray" style={{ marginTop: 4 }}>Not saved as a contact</HxChip>
            </>
          ) : (
            <HxChip tone="gray" style={{ marginTop: 4, textTransform: "capitalize" }}>{r.role}</HxChip>
          )}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginTop: 20 }}>
        <div><div className="hx-podium__stat-v">{calls}</div><div className="hx-podium__stat-l">Calls</div></div>
        <div><div className="hx-podium__stat-v" style={{ color: "#047857" }}>{r.completed}</div><div className="hx-podium__stat-l">Completed</div></div>
        <div><div className="hx-podium__stat-v">{fmtDuration(r.duration_total)}</div><div className="hx-podium__stat-l">Talk time</div></div>
      </div>
    </HxCard>
  );
}

function Podium({ rows }) {
  const top3 = rows.slice(0, 3);
  if (top3.length === 0) return null;
  // Classic podium order on desktop: silver · GOLD · bronze
  const order = top3.length === 3 ? [1, 0, 2] : top3.map((_, i) => i);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 hx-up hx-d1" style={{ paddingTop: 10 }} data-testid="leaderboard-podium">
      {order.map((rank) => <PodiumCard key={top3[rank].user_id} r={top3[rank]} rank={rank} />)}
    </div>
  );
}

/* ============================================================
 *  KPI band — 8 metric cells in one card
 * ============================================================ */
function KpiBand({ kpis, prev }) {
  const tiles = [
    { k: "calls_total",         label: "Total calls",     icon: Phone,         tone: C.blue,   fmt: (v) => v },
    { k: "calls_completed",     label: "Completed",       icon: CheckCircle,   tone: C.green,  fmt: (v) => `${v} (${kpis.calls_completion_rate}%)` },
    { k: "calls_inbound",       label: "Inbound",         icon: PhoneIncoming, tone: C.blue,   fmt: (v) => v },
    { k: "calls_outbound",      label: "Outbound",        icon: PhoneOutgoing, tone: C.violet, fmt: (v) => v },
    { k: "calls_missed",        label: "Missed",          icon: PhoneX,        tone: C.red,    fmt: (v) => v, invert: true },
    { k: "call_duration_total", label: "Total talk time", icon: Timer,         tone: C.orange, fmt: fmtDuration, dur: true },
    { k: "avg_talk_time",       label: "Avg talk time",   icon: Clock,         tone: C.orange, fmt: fmtDuration, dur: true },
    { k: "sms_total",           label: "SMS activity",    icon: ChatText,      tone: C.green,  fmt: (v) => v },
  ];
  return (
    <HxCard className="hx-up hx-d2" style={{ marginTop: 20, overflow: "hidden" }} data-testid="kpi-grid">
      <HxCardHead title="Performance metrics" right={<span style={{ fontSize: 12, fontWeight: 700, color: "var(--hx-ink-3)" }}>vs prior period</span>} />
      <div className="grid grid-cols-2 md:grid-cols-4" style={{ marginTop: 8 }}>
        {tiles.map((t, i) => {
          const v = kpis[t.k] ?? 0;
          const pv = prev?.[t.k];
          const delta = (pv != null) ? (v - pv) : null;
          const good = t.invert ? delta < 0 : delta > 0;
          return (
            <div
              key={t.k}
              style={{
                padding: "16px 22px 18px",
                borderTop: i >= 4 ? "1px solid var(--hx-line-soft)" : "none",
                borderRight: (i + 1) % 4 !== 0 ? "1px solid var(--hx-line-soft)" : "none",
              }}
              data-testid={`kpi-${t.k}`}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 11.5, fontWeight: 700, color: "var(--hx-ink-2)" }}>
                <t.icon size={14} weight="duotone" color={t.tone} /> {t.label}
              </div>
              <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.03em", marginTop: 6, fontVariantNumeric: "tabular-nums" }}>{t.fmt(v)}</div>
              {delta !== null && delta !== 0 && (
                <span className={`hx-delta ${good ? "hx-delta--up" : "hx-delta--down"}`} style={{ marginTop: 6, display: "inline-flex" }} data-testid={`kpi-delta-${t.k}`}>
                  {delta > 0 ? "▲" : "▼"} {t.dur ? fmtDuration(Math.abs(delta)) : Math.abs(delta).toLocaleString()}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </HxCard>
  );
}

/* ============================================================
 *  Ranked tables
 * ============================================================ */
function AgentCell({ r }) {
  if (r.unknown) {
    return (
      <div className="flex items-center gap-2.5">
        <div style={{ width: 30, height: 30, borderRadius: 10, background: "var(--hx-bg)", display: "grid", placeItems: "center", color: "var(--hx-ink-3)" }}>
          <Phone size={14} weight="duotone" />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
            {r.name}
            {r.phone && <span style={{ fontFamily: "var(--hx-mono)", fontSize: 11, fontWeight: 500, color: "var(--hx-ink-2)" }}>{r.phone}</span>}
          </div>
          <div style={{ fontSize: 10.5, color: "var(--hx-ink-3)" }}>Not saved as a contact</div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2.5">
      {r.avatar_url
        ? <img src={r.avatar_url} alt="" style={{ width: 30, height: 30, borderRadius: 10, objectFit: "cover" }} />
        : <div className="hx-feed__avatar" style={{ width: 30, height: 30, borderRadius: 10, fontSize: 11 }}>{initials(r.name)}</div>}
      <div>
        <div style={{ fontSize: 13, fontWeight: 700 }}>{r.name}</div>
        <div style={{ fontSize: 10.5, color: "var(--hx-ink-3)", fontFamily: "var(--hx-mono)" }}>{r.email}</div>
      </div>
    </div>
  );
}

function RankBadge({ n }) {
  const medal = n <= 3 ? ["#F5B301", "#B7C2D0", "#D08A4F"][n - 1] : null;
  return medal
    ? <span style={{ display: "inline-grid", placeItems: "center", width: 22, height: 22, borderRadius: 8, background: medal, color: "#fff", fontSize: 11, fontWeight: 800 }}>{n}</span>
    : <span style={{ fontSize: 12, color: "var(--hx-ink-3)", fontWeight: 700, paddingLeft: 7 }}>{n}</span>;
}

function TopCallers({ rows }) {
  const ranked = rows.map((r, i) => ({ ...r, _rank: i + 1 }));
  return (
    <HxCard data-testid="top-callers-card">
      <HxCardHead title="Top callers" icon={<Phone size={15} weight="duotone" color={C.blue} />} right={<HxChip tone="blue">by call volume</HxChip>} />
      {rows.length === 0 ? (
        <div className="hx-empty" data-testid="top-callers-empty">No calls in this window.</div>
      ) : (
        <div style={{ padding: "4px 8px 10px" }}>
          <DataTable
            testId="top-callers-table"
            data={ranked}
            rowKey={(r) => r.user_id}
            rowTestId={(r) => `top-caller-${r.user_id}`}
            searchable={false}
            pageSize={10}
            columns={[
              { key: "_rank", label: "#", sortable: true, width: "48px", render: (r) => <RankBadge n={r._rank} /> },
              { key: "name",  label: "Agent", sortable: true, render: (r) => <AgentCell r={r} /> },
              { key: "calls",          label: "Calls",     sortable: true, width: "72px",  align: "right", render: (r) => <span className="tabular-nums font-bold">{r.calls}</span> },
              { key: "completed",      label: "Compl.",    sortable: true, width: "72px",  align: "right", render: (r) => <span className="tabular-nums" style={{ color: "#047857", fontWeight: 700 }}>{r.completed}</span> },
              { key: "missed",         label: "Missed",    sortable: true, width: "72px",  align: "right", render: (r) => <span className="tabular-nums" style={{ color: "#B91C1C" }}>{r.missed}</span> },
              { key: "duration_total", label: "Talk time", sortable: true, width: "94px",  align: "right", render: (r) => <span className="tabular-nums">{fmtDuration(r.duration_total)}</span> },
              { key: "avg_talk_time",  label: "Avg",       sortable: true, width: "72px",  align: "right", render: (r) => <span className="tabular-nums" style={{ color: "var(--hx-ink-3)" }}>{fmtDuration(r.avg_talk_time)}</span> },
            ]}
          />
        </div>
      )}
    </HxCard>
  );
}

function TopSmsUsers({ rows }) {
  const ranked = rows.map((r, i) => ({ ...r, _rank: i + 1 }));
  return (
    <HxCard data-testid="top-sms-card">
      <HxCardHead title="Top SMS users" icon={<ChatText size={15} weight="duotone" color={C.green} />} right={<HxChip tone="green">by message volume</HxChip>} />
      {rows.length === 0 ? (
        <div className="hx-empty" data-testid="top-sms-empty">No SMS in this window.</div>
      ) : (
        <div style={{ padding: "4px 8px 10px" }}>
          <DataTable
            testId="top-sms-table"
            data={ranked}
            rowKey={(r) => r.user_id}
            rowTestId={(r) => `top-sms-${r.user_id}`}
            searchable={false}
            pageSize={10}
            columns={[
              { key: "_rank", label: "#", sortable: true, width: "48px", render: (r) => <RankBadge n={r._rank} /> },
              { key: "name", label: "Agent", sortable: true, render: (r) => <AgentCell r={r} /> },
              { key: "messages", label: "Total",    sortable: true, width: "72px", align: "right", render: (r) => <span className="tabular-nums font-bold">{r.messages}</span> },
              { key: "outbound", label: "Outbound", sortable: true, width: "94px", align: "right", render: (r) => <span className="tabular-nums">{r.outbound}</span> },
              { key: "inbound",  label: "Inbound",  sortable: true, width: "94px", align: "right", render: (r) => <span className="tabular-nums">{r.inbound}</span> },
            ]}
          />
        </div>
      )}
    </HxCard>
  );
}

/* ============================================================
 *  Daily call trend — custom SVG rounded bars (no chart lib)
 * ============================================================ */
function CallTrend({ series }) {
  const w = 680, h = 216, padL = 34, padR = 12, padT = 16, padB = 26;
  const max = Math.max(4, ...series.map((s) => s.calls));
  const n = Math.max(series.length, 1);
  const step = (w - padL - padR) / n;
  const bw = Math.min(30, step * 0.56);
  const y = (v) => padT + (1 - v / max) * (h - padT - padB);
  const y0 = y(0);
  const gridVals = [0, Math.round(max / 2), max];
  const labelEvery = Math.ceil(series.length / 12);

  return (
    <HxCard className="hx-up hx-d4" style={{ marginTop: 20 }} data-testid="call-trend-card">
      <HxCardHead title="Daily call volume" right={<span style={{ fontSize: 12, fontWeight: 700, color: "var(--hx-ink-3)" }}>{series.length} day{series.length === 1 ? "" : "s"} with activity</span>} />
      {series.length === 0 ? (
        <div className="hx-empty">No data in this window.</div>
      ) : (
        <>
          <div className="hx-chart">
            <svg viewBox={`0 0 ${w} ${h}`} data-testid="call-trend-chart">
              {gridVals.map((g) => (
                <g key={g}>
                  <line className="hx-chart-grid" x1={padL} x2={w - padR} y1={y(g)} y2={y(g)} />
                  <text x={padL - 8} y={y(g) + 4} textAnchor="end" fontSize="10" fill="#98A2B3" fontFamily="IBM Plex Mono, monospace">{g}</text>
                </g>
              ))}
              {series.map((s, i) => {
                const x = padL + i * step + (step - bw) / 2;
                const totalH = Math.max(3, y0 - y(s.calls));
                const inH = Math.round((s.inbound / Math.max(s.calls, 1)) * totalH);
                return (
                  <g key={s.date} className="hx-dot-pop" style={{ animationDelay: `${0.1 + i * 0.04}s` }} data-testid={`trend-bar-${s.date}`}>
                    <title>{`${s.date} · ${s.calls} calls (${s.inbound} in / ${s.outbound} out)`}</title>
                    <rect x={x} y={y0 - totalH} width={bw} height={totalH} rx="5" fill="#0B1526" opacity="0.88" />
                    {inH > 2 && <rect x={x} y={y0 - inH} width={bw} height={inH} rx="5" fill="#2563EB" />}
                    {(i % labelEvery === 0) && (
                      <text x={x + bw / 2} y={h - 8} textAnchor="middle" fontSize="10" fill="#98A2B3" fontWeight="600" fontFamily="Manrope, sans-serif">{s.date.slice(5)}</text>
                    )}
                  </g>
                );
              })}
            </svg>
          </div>
          <div className="hx-legend">
            <div className="hx-legend__item">
              <div className="hx-legend__key"><span className="hx-legend__dot" style={{ background: "#2563EB" }} /> Inbound</div>
              <div className="hx-legend__val">{series.reduce((a, s) => a + s.inbound, 0).toLocaleString()}</div>
            </div>
            <div className="hx-legend__item">
              <div className="hx-legend__key"><span className="hx-legend__dot" style={{ background: "#0B1526" }} /> Outbound</div>
              <div className="hx-legend__val">{series.reduce((a, s) => a + s.outbound, 0).toLocaleString()}</div>
            </div>
            <div className="hx-legend__item">
              <div className="hx-legend__key"><span className="hx-legend__dot" style={{ background: "#94A3B8" }} /> Total</div>
              <div className="hx-legend__val">{series.reduce((a, s) => a + s.calls, 0).toLocaleString()}</div>
            </div>
          </div>
        </>
      )}
    </HxCard>
  );
}
